import {
  OutreachSubscriberPushDtoType,
  OutreachSupportEmailDtoType,
  OutreachSupportEmailTriggerConfigDtoType,
  OutreachSupportEmailTriggerConfigDtoTypeTime,
} from './dtos/outreach-support-email.dto';
import {
  OutreachConfigFormFields,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachConfigSections,
  OutreachSubscriberConfigResponse,
  OutreachTimeFormFields,
  OutreachTriggerTypes,
  SaveOutreachWebhookConfig,
  SubscriberClearMessageFormFields,
  SubscriberContentFormFields,
  SubscriberOutreachContentFormFields,
  SubscriberOutreachTriggerFormFields,
  SupportOutreachClearMessageFormFields,
  SupportOutreachContentFormFields,
  SupportOutreachTriggerFormFields,
} from '../../types';
import { OutreachTypes } from '../../../../urls';
import { FrequencyUnits } from './helpers';
import { OutreachConfigFormDto } from '../../../Outreach/hooks/useOutreach';
import {
  getTriggerSection,
  getWebhookTriggerSection,
} from '../../../Outreach/helpers/trigger-outreach-helper';
import { FormikOutreachSupportTimeValue } from './controls/OutreachConfig/support/FormikOutreachSupportTimeField';

export type SectionRepresentation = {
  config: keyof OutreachConfigFormDto;
  content?: keyof OutreachConfigFormDto;
  time: keyof OutreachConfigFormDto;
  trigger: keyof OutreachConfigFormDto;
  clear?: keyof OutreachConfigFormDto;
};

const mapOutreachTypeToSectionName: Record<
  OutreachTypes,
  SectionRepresentation
> = {
  [OutreachTypes.Email]: {
    config: OutreachConfigSections.Config,
    content: OutreachConfigSections.SupportOutreachContent,
    time: OutreachConfigSections.SupportOutreachTime,
    trigger: OutreachConfigSections.SupportOutreachTrigger,
    clear: OutreachConfigSections.SupportOutreachClearMessage,
  },
  [OutreachTypes.Webhooks]: {
    config: OutreachConfigSections.Config,
    time: OutreachConfigSections.SupportOutreachTimeWebhook,
    trigger: OutreachConfigSections.SupportOutreachTriggerWebhook,
  },
  [OutreachTypes.Push]: {
    config: OutreachConfigSections.Config,
    content: OutreachConfigSections.SubscriberOutreachContent,
    time: OutreachConfigSections.SubscriberOutreachTime,
    trigger: OutreachConfigSections.SubscriberOutreachTrigger,
    clear: OutreachConfigSections.SubscriberOutreachClearMessage,
  },
};

type TriggerConfig = OutreachSupportEmailTriggerConfigDtoType;

export function getSection<N extends keyof OutreachConfigFormDto>(
  data: OutreachConfigFormDto,
  outreachType: OutreachTypes,
  sectionName: keyof SectionRepresentation,
): OutreachConfigFormDto[N] {
  const sectionNames = mapOutreachTypeToSectionName[outreachType];
  return data[
    sectionNames[sectionName] as keyof OutreachConfigFormDto
  ] as OutreachConfigFormDto[N];
}

export const prepareTimesForPostPayload = (
  timeSection: FormikOutreachSupportTimeValue,
) => {
  return {
    startTime:
      timeSection[OutreachTimeFormFields.From] != null
        ? timeSection[OutreachTimeFormFields.From]
        : null,
    endTime:
      timeSection[OutreachTimeFormFields.To] != null
        ? timeSection[OutreachTimeFormFields.To]
        : null,
  };
};

export const prepareScheduleForTimeTrigger = (
  data: OutreachConfigFormDto,
  outreachType: OutreachTypes,
  sectionName: keyof OutreachConfigFormDto,
): Pick<
  OutreachSupportEmailTriggerConfigDtoTypeTime['triggerRelatedInfo'],
  'schedule'
> => {
  const triggerSection = getSection<
    OutreachConfigSections.SupportOutreachTrigger
  >(data, outreachType, 'trigger');
  if (triggerSection.isOneTimeOnly) {
    return {
      schedule: {
        weekdays: [],
        recurrence: 0,
        recurrenceUnit: FrequencyUnits.Once,
      },
    };
  }
  return {
    schedule: {
      weekdays:
        triggerSection[SupportOutreachTriggerFormFields.TimeRepeatWeekdays] ||
        [],
      recurrence:
        triggerSection[SupportOutreachTriggerFormFields.TimeRepeatEvery] || 0,
      recurrenceUnit:
        triggerSection[SupportOutreachTriggerFormFields.TimeRepeatEveryUnit] ||
        FrequencyUnits.Hour,
    },
  };
};

export const getTriggerConfig = (
  data: OutreachConfigFormDto,
  outreachType: OutreachTypes,
  trigger: OutreachTriggerTypes,
): TriggerConfig => {
  const triggerSection = getSection<
    OutreachConfigSections.SupportOutreachTrigger
  >(data, outreachType, 'trigger');
  switch (trigger) {
    case OutreachTriggerTypes.Time:
      return {
        triggerType: OutreachTriggerTypes.Time,
        triggerRelatedInfo: prepareScheduleForTimeTrigger(
          data,
          outreachType,
          OutreachConfigSections.SupportOutreachContent,
        ),
      };
    case OutreachTriggerTypes.CSPOutageDetected:
      return {
        triggerType: OutreachTriggerTypes.CSPOutageDetected,
        triggerRelatedInfo: {
          issueClearedPercentage:
            triggerSection[SupportOutreachTriggerFormFields.IssueClearedWhen] ||
            0,
          minSubscribersAffected:
            triggerSection[
              SupportOutreachTriggerFormFields.IssueTriggeredWhen
            ] || 0,
        },
      };
    case OutreachTriggerTypes.MaxValue:
      return {
        triggerType: OutreachTriggerTypes.MaxValue,
        triggerRelatedInfo: {
          issueClearingLocationCount:
            triggerSection[SupportOutreachTriggerFormFields.IssueClearedWhen] ||
            0,
          issueTriggeringLocationCount:
            triggerSection[
              SupportOutreachTriggerFormFields.IssueTriggeredWhen
            ] || 0,
        },
      };
    case OutreachTriggerTypes.MaxIncreaseRate:
      return {
        triggerType: OutreachTriggerTypes.MaxIncreaseRate,
        triggerRelatedInfo: {
          issueClearingLocationPercentage:
            triggerSection[SupportOutreachTriggerFormFields.IssueClearedWhen] ||
            0,
          issueTriggeringLocationPercentage:
            triggerSection[
              SupportOutreachTriggerFormFields.IssueTriggeredWhen
            ] || 0,
        },
      };
    case OutreachTriggerTypes.MaxPercentage:
      return {
        triggerType: OutreachTriggerTypes.MaxPercentage,
        triggerRelatedInfo: {
          issueClearingLocationPercentage:
            triggerSection[SupportOutreachTriggerFormFields.IssueClearedWhen] ||
            0,
          issueTriggeringLocationPercentage:
            triggerSection[
              SupportOutreachTriggerFormFields.IssueTriggeredWhen
            ] || 0,
        },
      };
  }
};

export const sanitizeMaxNotificationsAllowedUnits = (unit: FrequencyUnits) => {
  return unit === FrequencyUnits.Hour ? 'hr' : unit;
};

export const preparePostPayloadForSupport = (
  data: OutreachConfigFormDto,
): OutreachSupportEmailDtoType => {
  const configSection = getSection<OutreachConfigSections.Config>(
    data,
    OutreachTypes.Email,
    'config',
  );
  const timeSection = getSection<OutreachConfigSections.SupportOutreachTime>(
    data,
    OutreachTypes.Email,
    'time',
  );
  const triggerSection = getSection<
    OutreachConfigSections.SupportOutreachTrigger
  >(data, OutreachTypes.Email, 'trigger');
  const contentSection = getSection<
    OutreachConfigSections.SupportOutreachContent
  >(data, OutreachTypes.Email, 'content');
  const clearMessageSection = getSection<
    OutreachConfigSections.SupportOutreachClearMessage
  >(data, OutreachTypes.Email, 'clear');
  const triggerConfig = getTriggerConfig(
    data,
    OutreachTypes.Email,
    triggerSection[SupportOutreachTriggerFormFields.TriggerType],
  );
  const timeConfig = prepareTimesForPostPayload(timeSection);
  const payload = {
    ruleName: contentSection[SupportOutreachContentFormFields.OutreachRuleName],
    enabled: configSection[OutreachConfigFormFields.SupportOutreachEnabled],
    ...timeConfig,
    triggerConfig,
    maxNotificationsAllowed: {
      recurrenceUnit: sanitizeMaxNotificationsAllowedUnits(
        triggerSection[
          SupportOutreachTriggerFormFields
            .ScheduleMaxFrequencyOfSupportOutreachUnit
        ],
      ),
      recurrence:
        triggerSection[
          SupportOutreachTriggerFormFields
            .ScheduleMaxFrequencyOfSupportOutreachEvery
        ] || 0,
      maxAllowed:
        triggerSection[
          SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreach
        ] || 0,
    },
    notifyIssueClearMessage:
      triggerSection[
        SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared
      ],
    issueMessageConfig: {
      recipients: contentSection[SupportOutreachContentFormFields.Recipients],
      subject: contentSection[SupportOutreachContentFormFields.Subject],
      introduction:
        contentSection[SupportOutreachContentFormFields.Introduction],
    },
  };

  if (
    triggerSection[
      SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared
    ]
  ) {
    return {
      ...payload,
      clearMessageConfig: {
        introduction: clearMessageSection![
          SupportOutreachClearMessageFormFields.Introduction
        ],
        subject: clearMessageSection![
          SupportOutreachClearMessageFormFields.Subject
        ],
        recipients: clearMessageSection![
          SupportOutreachClearMessageFormFields.Recipients
        ],
      },
    };
  }
  return payload;
};

export const preparePostPayloadForSupportWebhook = (
  data: OutreachConfigFormDto,
): SaveOutreachWebhookConfig => {
  const configSection = getSection<OutreachConfigSections.Config>(
    data,
    OutreachTypes.Webhooks,
    'config',
  );
  const timeSection = getSection<
    OutreachConfigSections.SupportOutreachTimeWebhook
  >(data, OutreachTypes.Webhooks, 'time');
  const triggerSection = getSection<
    OutreachConfigSections.SupportOutreachTriggerWebhook
  >(data, OutreachTypes.Webhooks, 'trigger');
  const triggerConfig = getTriggerConfig(
    data,
    OutreachTypes.Webhooks,
    triggerSection[SupportOutreachTriggerFormFields.TriggerType],
  );
  const timeConfig = prepareTimesForPostPayload(timeSection);
  const payload = {
    ruleName: configSection[OutreachConfigFormFields.SelectedWebhookName],
    enabled: configSection[OutreachConfigFormFields.WebhookOutreachEnabled],
    ...timeConfig,
    triggerConfig,
    notifyIssueClearMessage:
      triggerSection[
        SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared
      ],
    issueMessageConfig: {
      webhooks: configSection[OutreachConfigFormFields.SelectedWebhook],
    },
  };
  return payload;
};

export const preparePostPayloadForSubscriber = (
  data: OutreachConfigFormDto,
): OutreachSubscriberPushDtoType => {
  const configSection = getSection<OutreachConfigSections.Config>(
    data,
    OutreachTypes.Push,
    'config',
  );
  const timeSection = getSection<OutreachConfigSections.SubscriberOutreachTime>(
    data,
    OutreachTypes.Push,
    'time',
  );
  const triggerSection = getSection<
    OutreachConfigSections.SubscriberOutreachTrigger
  >(data, OutreachTypes.Push, 'trigger');
  const contentSection = getSection<
    OutreachConfigSections.SubscriberOutreachContent
  >(data, OutreachTypes.Push, 'content');
  const clearMessageSection = getSection<
    OutreachConfigSections.SubscriberOutreachClearMessage
  >(data, OutreachTypes.Push, 'clear');
  const triggerConfig = getTriggerConfig(
    data,
    OutreachTypes.Push,
    triggerSection[SupportOutreachTriggerFormFields.TriggerType],
  );
  const timeConfig = prepareTimesForPostPayload(timeSection);
  const payload = {
    ruleName: contentSection[SupportOutreachContentFormFields.OutreachRuleName],
    enabled: configSection[OutreachConfigFormFields.SubscriberOutreachEnabled],
    ...timeConfig,
    triggerConfig,
    maxNotificationsAllowed: {
      recurrenceUnit: sanitizeMaxNotificationsAllowedUnits(
        triggerSection[
          SupportOutreachTriggerFormFields
            .ScheduleMaxFrequencyOfSupportOutreachUnit
        ],
      ),
      recurrence:
        triggerSection[
          SupportOutreachTriggerFormFields
            .ScheduleMaxFrequencyOfSupportOutreachEvery
        ] || 0,
      maxAllowed:
        triggerSection[
          SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreach
        ] || 0,
    },
    notifyIssueClearMessage:
      triggerSection[
        SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared
      ],
    issueMessageConfig: {
      subject: contentSection[SubscriberContentFormFields.Subject],
      message: contentSection[SubscriberContentFormFields.Message],
      url: {
        enabled: contentSection[SubscriberContentFormFields.UrlEnabled],
        linkReference:
          contentSection[SubscriberContentFormFields.UrlLinkReference],
        urlText: contentSection[SubscriberContentFormFields.UrlText],
        urlLink: contentSection[SubscriberContentFormFields.UrlLink],
      },
    },
  };

  if (
    triggerSection[
      SubscriberOutreachTriggerFormFields.SendClearEmailWhenIssueCleared
    ]
  ) {
    return {
      ...payload,
      clearMessageConfig: {
        message: clearMessageSection![SubscriberClearMessageFormFields.Message],
        subject: clearMessageSection![SubscriberClearMessageFormFields.Subject],
      },
    };
  }
  return payload;
};

export const prepareFormData = (
  configEmail: OutreachConfigResponse,
  configWebhook: OutreachConfigResponseWebhook,
  formDto: OutreachConfigFormDto,
  configPush: OutreachSubscriberConfigResponse,
): OutreachConfigFormDto => {
  const triggerSection = getTriggerSection(configEmail);
  const triggerSubscriberSection = getTriggerSection(configPush);
  const triggerWebhookSection = getWebhookTriggerSection(configWebhook);

  return {
    ...formDto,
    [OutreachConfigSections.Config]: {
      ...formDto.outreachConfig,
      [OutreachConfigFormFields.SupportOutreach]: Boolean(
        configEmail !== undefined,
      ),
      [OutreachConfigFormFields.SubscriberOutreach]: Boolean(
        configPush !== undefined,
      ),
      [OutreachConfigFormFields.WebhookOutreach]: Boolean(
        configWebhook !== undefined,
      ),
      [OutreachConfigFormFields.SupportOutreachEnabled]: Boolean(
        configEmail?.enabled,
      ),
      [OutreachConfigFormFields.SubscriberOutreachEnabled]: Boolean(
        configPush?.enabled,
      ),
      [OutreachConfigFormFields.WebhookOutreachEnabled]: Boolean(
        configWebhook?.enabled,
      ),
      [OutreachConfigFormFields.SelectedWebhook]:
        configWebhook?.issueMessageConfig?.webhooks || [],
      [OutreachConfigFormFields.SelectedWebhookName]: configWebhook?.ruleName,
    },

    // support
    [OutreachConfigSections.SupportOutreachTime]: {
      ...formDto.supportOutreachTime,
      [OutreachTimeFormFields.From]: configEmail?.startTime
        ? new Date(configEmail.startTime)
        : undefined,
      [OutreachTimeFormFields.To]: configEmail?.endTime
        ? new Date(configEmail.endTime)
        : undefined,
    },
    [OutreachConfigSections.SupportOutreachTrigger]: {
      ...formDto.supportOutreachTrigger,
      ...triggerSection,
    },
    [OutreachConfigSections.SupportOutreachContent]: {
      ...formDto.supportOutreachContent,
      [SupportOutreachContentFormFields.OutreachRuleName]:
        configEmail?.ruleName,
      [SupportOutreachContentFormFields.Recipients]: configEmail
        ?.issueMessageConfig?.recipients || [''],
      [SupportOutreachContentFormFields.Subject]:
        configEmail?.issueMessageConfig.subject,
      [SupportOutreachContentFormFields.Introduction]:
        configEmail?.issueMessageConfig.introduction,
    },
    [OutreachConfigSections.SupportOutreachClearMessage]: {
      ...formDto?.supportOutreachClearMessage,
      [SupportOutreachClearMessageFormFields.Recipients]: configEmail
        ?.clearMessageConfig?.recipients || [''],
      [SupportOutreachClearMessageFormFields.Subject]:
        configEmail?.clearMessageConfig?.subject || '',
      [SupportOutreachClearMessageFormFields.Introduction]:
        configEmail?.clearMessageConfig?.introduction || '',
    },

    //webhook
    [OutreachConfigSections.SupportOutreachTimeWebhook]: {
      ...formDto.supportOutreachTimeWebhook,
      [OutreachTimeFormFields.From]: configWebhook?.startTime
        ? new Date(configWebhook.startTime)
        : undefined,
      [OutreachTimeFormFields.To]: configWebhook?.endTime
        ? new Date(configWebhook.endTime)
        : undefined,
    },
    [OutreachConfigSections.SupportOutreachTriggerWebhook]: {
      ...formDto.supportOutreachTriggerWebhook,
      ...triggerWebhookSection,
    },

    // subscriber
    [OutreachConfigSections.SubscriberOutreachTime]: {
      ...formDto.subscriberOutreachTime,
      [OutreachTimeFormFields.From]: configPush?.startTime
        ? new Date(configPush.startTime)
        : undefined,
      [OutreachTimeFormFields.To]: configPush?.endTime
        ? new Date(configPush.endTime)
        : undefined,
    },
    [OutreachConfigSections.SubscriberOutreachTrigger]: {
      ...formDto.subscriberOutreachTrigger,
      ...triggerSubscriberSection,
    },
    [OutreachConfigSections.SubscriberOutreachContent]: {
      ...formDto.subscriberOutreachContent,
      [SubscriberOutreachContentFormFields.OutreachRuleName]:
        configPush?.ruleName || '',
      [SubscriberOutreachContentFormFields.Subject]:
        configPush?.issueMessageConfig?.subject || '',
      [SubscriberOutreachContentFormFields.Message]:
        configPush?.issueMessageConfig?.message || '',
      [SubscriberOutreachContentFormFields.UrlEnabled]:
        configPush?.issueMessageConfig?.url.enabled || false,
      [SubscriberOutreachContentFormFields.LinkReference]:
        configPush?.issueMessageConfig?.url?.linkReference || '',
      [SubscriberOutreachContentFormFields.UrlText]:
        configPush?.issueMessageConfig?.url?.urlText || '',
      [SubscriberOutreachContentFormFields.UrlLink]:
        configPush?.issueMessageConfig?.url?.urlLink || '',
    },
    [OutreachConfigSections.SubscriberOutreachClearMessage]: {
      ...formDto?.supportOutreachClearMessage,
      [SubscriberClearMessageFormFields.Subject]:
        configPush?.clearMessageConfig?.subject || '',
      [SubscriberClearMessageFormFields.Message]:
        configPush?.clearMessageConfig?.message || '',
    },
  };
};
